import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { NgbDateStruct, NgbTabset } from '@cmps/ng-bootstrap';
import { IOption } from '@cmps/ng-select';
import { forkJoin, Subscription } from 'rxjs';
import { concatMap, finalize, map, take, tap } from 'rxjs/operators';
import { utils, WorkBook, WorkSheet, writeFile } from 'xlsx';

import { Util } from '../../common/common.util';
import { TranslationKeyMap } from '../reports.i18n';
import { ReportsService } from '../reports.service';

import {
    TdDailyHuddleMetric,
    TdDailyHuddleMetricRow,
    TdDailyHuddlePeriodRange,
    TdDailyHuddleReportResultParams,
    TdDailyHuddleReportSelectionParams,
    TdDailyHuddleResult,
    TdDailyHuddleRole,
    TdDailyHuddleRow,
    TdDailyHuddleUser,
    TdRawAgentDataDates,
} from './td-daily-huddle.reports.model';

enum Tabs {
    PARAMS = 'w-tab-report-0',
    GRID = 'w-tab-report-1',
}

export function toCamel<TOut>(obj: { [key: string]: any }): TOut | TOut[] {
    let newObj: { [key: string]: any };
    let origKey: string;
    let newKey: string;
    let value: any;
    if (obj instanceof Array) {
        return obj.map(function (val) {
            if (typeof val === 'object') {
                val = toCamel<TOut>(val);
            }
            return val;
        }) as TOut[];
    } else {
        newObj = {};
        for (origKey in obj) {
            // eslint-disable-next-line no-prototype-builtins
            if (obj.hasOwnProperty(origKey)) {
                newKey = (
                    origKey.charAt(0).toLowerCase() + origKey.slice(1) ||
                    origKey
                ).toString();
                value = obj[origKey];
                if (
                    value instanceof Array ||
                    (value !== null && value.constructor === Object)
                ) {
                    value = toCamel(value);
                }
                newObj[newKey] = value;
            }
        }
    }
    return newObj as TOut;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function isValidDateStruct(paramsDate: NgbDateStruct) {
    if (
        paramsDate &&
        !isNaN(paramsDate.day) &&
        !isNaN(paramsDate.month) &&
        !isNaN(paramsDate.year)
    ) {
        return true;
    }
    return false;
}

@Component({
    selector: '[tdDailyHuddleReport]',
    templateUrl: './td-daily-huddle.reports.component.html',
    host: {
        class: 'cmps-reports-td-daily-huddle',
    },
})
export class TdDailyHuddleReportComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    @ViewChild('tabSet', { static: true }) tabSet: NgbTabset;
    @ViewChild('table') table: ElementRef<HTMLTableElement>;
    i18n = TranslationKeyMap;
    pendingData: boolean;
    noData: boolean = false;
    reportId: number = null;
    periods = ['WTD', 'MTD', 'QTD', 'YTD'];
    selectedParams: TdDailyHuddleReportSelectionParams = {
        date: null,
        metrics: [],
        role: null,
        user: null,
        noData: false,
        invalidUser: false,
        invalidDate: false,
        pendingData: false,
    };

    params: TdDailyHuddleReportResultParams = {
        date: null,
        metricList: null,
        requestUserID: null,
        roleID: null,
        userID: null,
        dateRequest: null,
    };

    availableMetrics: TdDailyHuddleMetric[] = [];
    result: TdDailyHuddleMetricRow[] = [];
    metrics: TdDailyHuddleMetric[] = [];
    roles: IOption[] = [];
    dataRanges: TdDailyHuddlePeriodRange[] = [];
    rawDates: TdRawAgentDataDates;
    sub: Subscription;
    selectedRole: TdDailyHuddleRole;
    selectedUser: TdDailyHuddleUser;

    constructor(private readonly _reportService: ReportsService) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnInit() {
        this.selectedParams.pendingData = true;
        forkJoin([
            this._reportService.retrieveConfigReportSelectedJson(1).pipe(
                take(1),
                map((config) => {
                    const params = config.kpiSelectionControls[0].parameters;
                    const metricsOutcomes = params.find(
                        (p) => p.name === 'metricsOutcomes',
                    ).value as number[];
                    const metricsBehaviours = params.find(
                        (p) => p.name === 'metricsBehaviours',
                    ).value as number[];
                    return metricsOutcomes.concat(metricsBehaviours);
                }),
                concatMap((metricIds) =>
                    this._reportService.retrieveMetricNames(
                        metricIds.join(','),
                    ),
                ),
                tap((metrics) => {
                    this.availableMetrics = metrics;
                }),
            ),
            this._reportService.retrieveRawAgentDataDates().pipe(
                take(1),
                tap((rawDates) => {
                    this.rawDates = rawDates;
                    this.selectedParams = {
                        ...this.selectedParams,
                        date: Util.dateToNgbDate(
                            new Date(this.rawDates.lastDate),
                        ),
                    };
                }),
            ),
            this._reportService.retrieveDefaultHierarchyUser().pipe(
                take(1),
                tap((user) => {
                    this.selectedParams = { ...this.selectedParams, user };
                }),
            ),
        ])
            .pipe(
                tap(() => {
                    this.onChanged();
                }),
                finalize(() => {
                    // this.onGeneratedReport(3441);
                    this.selectedParams.pendingData = false;
                }),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngAfterViewInit() {
        this.sub = this.tabSet.tabChange
            .pipe(
                tap((t) => {
                    if (t.nextId === Tabs.GRID) {
                        this.getDailyHuddleReportResult();
                    }
                }),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getDailyHuddleReportResult() {
        if (!this.reportId) {
            this.noData = true;
            return;
        }
        this.pendingData = true;
        this._reportService
            .retrieveGenericReportResult(this.reportId)
            .pipe(
                take(1),
                tap((result) => {
                    this.result = [];
                    this.metrics = [];
                    const metrics = this.availableMetrics;
                    if (result.resultSet && result.resultSet.length > 0) {
                        this.getParams(result);
                        this.dataRanges = this.getDateRanges(result);
                        const data = this.getSummary(
                            toCamel(
                                JSON.parse(result.resultSet),
                            ) as TdDailyHuddleRow[],
                        );
                        this.getRoleName();
                        this.getUserName();
                        this.processGridData(data, metrics);
                        this.noData = false;
                    }
                }),
                // eslint-disable-next-line no-return-assign
                finalize(() => (this.pendingData = false)),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private getSummary(data: TdDailyHuddleRow[]) {
        const summary = data.findIndex(
            (d) => d.userId === parseInt(this.params.userID),
        );
        if (summary > -1) {
            return [
                ...data.splice(summary, 1),
                ...data.sort((a, b) => {
                    return a.userName.toLowerCase() < b.userName.toLowerCase()
                        ? -1
                        : 1;
                }),
            ];
        }
        return data.sort((a, b) => {
            return a.userName.toLowerCase() < b.userName.toLowerCase() ? -1 : 1;
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onChanged() {
        if (this.selectedParams.user && this.selectedParams.date) {
            this.selectedParams.pendingData = true;
            this._reportService
                .retrieveRolesHierarchy(
                    this.selectedParams.user.userId,
                    Util.dateToDate(this.selectedParams.date),
                )
                .pipe(
                    take(1),
                    map((roles) =>
                        roles.map((r) => ({
                            value: r.id.toString(),
                            label: r.name,
                        })),
                    ),
                    tap((roles) => {
                        this.roles = roles;
                        if (roles.length > 0) {
                            this.selectedParams.role = roles[0].value;
                        }
                    }),
                    // eslint-disable-next-line no-return-assign
                    finalize(() => (this.selectedParams.pendingData = false)),
                )
                .subscribe();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onGeneratedReport(reportId: number) {
        this.reportId = reportId;
        this.tabSet.select('w-tab-report-1');
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private getParams(result: TdDailyHuddleResult) {
        this.params = result.params.split('@').reduce((output, current) => {
            const keyValue = current.split('=').map((v) => v.trim());
            const key = (
                keyValue[0].charAt(0).toLowerCase() + keyValue[0].slice(1) ||
                keyValue[0]
            ).toString();
            const value = keyValue[1];
            if (key) {
                output[key] = value.replace(/^,+|,+$/g, '');
            }
            return output;
        }, {}) as any;
    }

    private getDateRanges(
        result: TdDailyHuddleResult,
    ): TdDailyHuddlePeriodRange[] {
        return (
            toCamel(JSON.parse(result.periods)) as TdDailyHuddlePeriodRange[]
        ).map((periodRange) => {
            const startDate = new Date(periodRange.startDate);
            const endDate = new Date(periodRange.endDate);
            return {
                periodName: periodRange.periodName,
                startDate: new Date(
                    startDate.getTime() + startDate.getTimezoneOffset() * 60000,
                ),
                endDate: new Date(
                    endDate.getTime() + endDate.getTimezoneOffset() * 60000,
                ),
            };
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private getUserName() {
        this._reportService
            .retrieveUsersNameById(this.params.userID)
            .pipe(
                take(1),
                tap((r) => {
                    if (r.length > 0) {
                        this.selectedUser = r[0];
                    }
                }),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private getRoleName() {
        this._reportService
            .retrieveRolesNameById(this.params.roleID)
            .pipe(
                take(1),
                tap((r) => {
                    if (r.length > 0) {
                        this.selectedRole = r[0];
                    }
                }),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private processGridData(
        data: TdDailyHuddleRow[],
        metrics: TdDailyHuddleMetric[],
    ) {
        data.forEach((row) => {
            const metric = metrics.find((m) => m.id === parseInt(row.metricID));
            if (metric && !this.metrics.some((m) => m.id === metric.id)) {
                this.metrics.push(metric);
            }
        });
        this.result = data.reduce((p, c) => {
            const row = p.find((m) => m.userId === c.userId);
            if (row) {
                row.metrics.push({
                    metricID: c.metricID,
                    actual: c.actual,
                    periodName: c.periodName,
                });
            } else {
                p.push({
                    userId: c.userId,
                    userName: c.userName,
                    metrics: [
                        {
                            metricID: c.metricID,
                            actual: c.actual,
                            periodName: c.periodName,
                        },
                    ],
                });
            }
            return p;
        }, this.result);
        this.periods.forEach((period, periodIndex) => {
            this.metrics.forEach((metric, metricIndex) => {
                this.result.forEach((u) => {
                    const currMetric = u.metrics.find(
                        (m) =>
                            m.periodName === period &&
                            m.metricID === metric.id.toString(),
                    );
                    if (!currMetric) {
                        const index =
                            periodIndex * this.metrics.length + metricIndex;
                        u.metrics.splice(index, 0, {
                            metricID: metric.id.toString(),
                            actual: null,
                            periodName: period,
                        });
                    }
                });
            });
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onExportClick() {
        const workSheet: WorkSheet = utils.table_to_sheet(
            this.table.nativeElement,
        );
        const workBook: WorkBook = utils.book_new();
        utils.book_append_sheet(workBook, workSheet, 'Sheet1');
        writeFile(workBook, 'Report Daily Huddle.xlsx', {
            bookType: 'xlsx',
            type: 'buffer',
        });
    }
}
